import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '../ui/Dialog';
import Button from '../ui/Button';
import type { PendingActionItem } from '../../api/pendingActions';
import { sourceMeta } from './sourceMeta';
import { formatHours, formatMinutes } from '../reports/reportFormat';

interface DetailDialogProps {
  item: PendingActionItem | null;
  locale: string;
  onClose: () => void;
}

interface OnboardingTaskMeta {
  id: number;
  name: string;
  isBlocking?: boolean;
  isPolicy?: boolean;
}

function parseOnboardingTasks(raw: string | undefined): OnboardingTaskMeta[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Read-only context for an inbox item. Only the FALLBACK deep dive: Details
 * navigates straight to the source's page when one is accessible, so this
 * dialog renders exclusively for pageless sources (short leave, encashment,
 * assets, referee) and role-blocked targets — never with a page hand-off.
 */
const DetailDialog: React.FC<DetailDialogProps> = ({ item, locale, onClose }) => {
  const { t } = useTranslation('requests');

  if (!item) return null;
  const meta = item.meta ?? {};

  const fmtDate = (iso: string | undefined, withTime = false): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    });
  };

  /**
   * Clock times on one known day: the date is already its own row, so repeating it on every
   * time would push the pair past the dialog's width and say nothing new. Anything unparseable
   * is shown verbatim rather than as "Invalid Date" — the same rule fmtDate holds.
   */
  const fmtTime = (iso: string | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  };

  /** `a – b` only when both halves exist; a lone half is still worth showing. */
  const range = (from: string | undefined, to: string | undefined): string | undefined => {
    const a = fmtTime(from);
    const b = fmtTime(to);
    if (a && b) return `${a} – ${b}`;
    return a || b || undefined;
  };

  // Per-source rows, mirroring the mobile detail sheet's field selection.
  const rows: { label: string; value: string }[] = [];
  const push = (label: string, value: string | undefined) => {
    if (value) rows.push({ label, value });
  };

  switch (item.source) {
    case 'Leave':
      push(t('Dates'), meta.startDate && meta.endDate ? `${fmtDate(meta.startDate)} – ${fmtDate(meta.endDate)}` : undefined);
      push(t('Days'), meta.numberOfDays);
      push(t('Type'), meta.leaveType);
      if (meta.action === 'cancellation') push(t('Cancellation requested'), fmtDate(meta.cancelledDate, true));
      break;
    case 'ShiftSwap':
    case 'ShiftSwapAcceptance':
      push(t('With'), meta.counterpartyName);
      push(t('Their shift'), fmtDate(meta.requesterShiftStart, true));
      push(t('In return for'), fmtDate(meta.counterpartyShiftStart, true));
      if (meta.autoAppliesOnAccept === 'true') push(t('On accept'), t('Applies immediately'));
      break;
    case 'Claim':
      push(t('Amount'), meta.currency && meta.amount ? `${meta.currency} ${meta.amount}` : meta.amount);
      push(t('Category'), meta.claimType);
      break;
    case 'AttendanceCorrection':
      push(t('Date'), meta.date);
      push(t('Type'), meta.type);
      push(t('Requested check-in'), meta.requestedCheckIn);
      push(t('Requested check-out'), meta.requestedCheckOut);
      break;
    case 'OutOfBoundsClock':
      push(t('Date'), fmtDate(meta.date));
      push(t('Distance'), meta.distanceMeters ? t('{{meters}} m outside the allowed area', { meters: meta.distanceMeters }) : undefined);
      push(t('Reason'), meta.reason);
      break;
    case 'ShortLeave':
      push(t('Date'), fmtDate(meta.date));
      push(t('Time'), meta.startTime && meta.endTime ? `${meta.startTime} – ${meta.endTime}` : undefined);
      push(t('Hours'), meta.hours);
      break;
    case 'Encashment':
      push(t('Type'), meta.leaveTypeName);
      push(t('Days'), meta.days);
      push(t('Balance'), meta.balanceBefore && meta.balanceAfter ? `${meta.balanceBefore} → ${meta.balanceAfter}` : undefined);
      break;
    case 'AssetRequest':
      push(t('Category'), meta.categoryName);
      push(t('Priority'), meta.priority);
      push(t('Department'), meta.department);
      break;
    case 'Referee':
      push(t('Tournament'), meta.tournamentName);
      push(t('Match'), meta.team1Name && meta.team2Name ? `${meta.team1Name} – ${meta.team2Name}` : undefined);
      push(t('Scheduled'), fmtDate(meta.scheduledDateTime, true));
      push(t('Respond by'), fmtDate(meta.responseDeadline, true));
      push(t('Role'), meta.role);
      break;
    case 'ReturnToWork':
      push(t('Leave type'), meta.leaveType);
      push(t('Due'), fmtDate(meta.dueDate));
      break;
    case 'Survey':
      push(t('Month'), fmtDate(meta.month));
      break;
    case 'Terms':
      push(t('Version'), meta.version);
      break;
    // CO-583 — the inbox is the ONLY surface a reviewer without the queue's role (or on a Free
    // tenant, where the deep link is withheld) ever sees this shift on, and "approve overtime"
    // is a wage decision. Showing the row with no figures asked them to approve a number they
    // were never told. Scheduled is optional on purpose: a rest-day shift has no schedule, and
    // an absent row is the honest way to say so rather than an invented 00:00.
    case 'OvertimeApproval':
      push(t('Date'), fmtDate(meta.date));
      push(t('Clocked'), range(meta.clockIn, meta.clockOut));
      push(t('Scheduled'), range(meta.scheduledStart, meta.scheduledEnd));
      push(t('Early'), meta.earlyMinutes ? t('{{minutes}} min early', { minutes: meta.earlyMinutes }) : undefined);
      push(t('Late'), meta.lateMinutes ? t('{{minutes}} min late', { minutes: meta.lateMinutes }) : undefined);
      push(t('Overtime'), meta.overtimeHours ? formatHours(Number(meta.overtimeHours)) : undefined);
      break;
    // Hourly pay §9 — an approver without the queue's route (or on a Free tenant, where the deep
    // link is withheld) decides from here, so show the hours they are approving. No money: approvers
    // see hours, not amounts (§6).
    case 'ShiftPayApproval':
      push(t('Date'), fmtDate(meta.workDate));
      push(t('Type'), meta.kind === 'Unscheduled' ? t('Unscheduled work') : meta.kind === 'Scheduled' ? t('Scheduled shift') : undefined);
      push(t('Worked'), meta.workedMinutes ? formatMinutes(Number(meta.workedMinutes)) : undefined);
      push(t('Review'), meta.isClean === 'true' ? t('Clean') : meta.isClean === 'false' ? t('Needs attention') : undefined);
      break;
    default:
      break;
  }
  push(t('Status'), item.status);
  push(t('Submitted'), fmtDate(item.submittedOn, true));

  const onboardingTasks = item.source === 'Onboarding' ? parseOnboardingTasks(meta.tasks) : [];

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t(sourceMeta(item.source).label)}</DialogTitle>
          <DialogDescription>
            {[item.actorName, item.title].filter(Boolean).join(' · ')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <dl className="space-y-2.5">
            {rows.map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-4">
                <dt className="flex-none text-xs font-medium text-muted-foreground">{row.label}</dt>
                <dd className="text-end text-sm text-foreground">{row.value}</dd>
              </div>
            ))}
          </dl>
          {onboardingTasks.length > 0 && (
            <ul className="mt-3 space-y-1.5 border-t border-border pt-3">
              {onboardingTasks.map((task) => (
                <li key={task.id} className="flex items-center gap-2 text-sm text-foreground">
                  {task.isBlocking ? (
                    <AlertCircle size={14} className="flex-none text-amber-600" aria-hidden="true" />
                  ) : (
                    <span className="h-1.5 w-1.5 flex-none rounded-full bg-muted-foreground/50" aria-hidden="true" />
                  )}
                  <span className="truncate">{task.name}</span>
                </li>
              ))}
            </ul>
          )}
          {item.reason && (
            <p className="mt-3 border-t border-border pt-3 text-sm text-muted-foreground">{item.reason}</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DetailDialog;
